import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [clientCode, setClientCode] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError("");
      if (!clientCode.trim()) {
        setError("请输入客户编码");
        return;
      }
      if (!password.trim()) {
        setError("请输入密码");
        return;
      }
      setLoading(true);
      const res = await login(clientCode.trim(), password.trim());
      setLoading(false);
      if (res.success) {
        navigate("/dashboard");
      } else {
        setError(res.error || "登录失败");
      }
    },
    [clientCode, password, login, navigate]
  );

  return (
    <div className="min-h-screen bg-[#f8f5f0] flex flex-col items-center justify-center px-5 py-8">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-10">
          <div className="w-20 h-20 rounded-2xl overflow-hidden mb-5 bg-white shadow-sm flex items-center justify-center p-2">
            <img
              src="/logo.png"
              alt="RTB Logo"
              className="w-full h-full object-contain"
            />
          </div>
          <h1 className="text-xl font-bold text-[#1a3a5c] tracking-wide">
            RTB 客户服务中心
          </h1>
          <div className="flex items-center justify-center gap-2 mt-1.5">
            <img src="/flags/kg.png" alt="吉尔吉斯斯坦" title="吉尔吉斯斯坦" className="w-7 h-5 rounded-[3px] object-cover ring-1 ring-black/10" />
            <img src="/flags/ge.png" alt="格鲁吉亚" title="格鲁吉亚" className="w-7 h-5 rounded-[3px] object-cover ring-1 ring-black/10" />
            <img src="/flags/my.png" alt="马来西亚" title="马来西亚" className="w-7 h-5 rounded-[3px] object-cover ring-1 ring-black/10" />
            <img src="/flags/jp.png" alt="日本" title="日本" className="w-7 h-5 rounded-[3px] object-cover ring-1 ring-black/10" />
            <img src="/flags/co.png" alt="哥伦比亚" title="哥伦比亚" className="w-7 h-5 rounded-[3px] object-cover ring-1 ring-black/10" />
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8">
          <h2 className="text-base font-semibold text-[#1a3a5c] mb-5 text-center">
            客户登录
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">
                客户编码
              </label>
              <div className="relative">
                <i className="ri-barcode-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  type="text"
                  value={clientCode}
                  onChange={(e) => setClientCode(e.target.value)}
                  placeholder="请输入客户编码"
                  className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-gray-200 text-sm text-[#1a3a5c] placeholder:text-gray-300 focus:outline-none focus:border-[#c9a96e] focus:ring-1 focus:ring-[#c9a96e]/20 transition-all bg-white"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">
                密码
              </label>
              <div className="relative">
                <i className="ri-lock-password-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="请输入密码"
                  className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-gray-200 text-sm text-[#1a3a5c] placeholder:text-gray-300 focus:outline-none focus:border-[#c9a96e] focus:ring-1 focus:ring-[#c9a96e]/20 transition-all bg-white"
                />
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-1.5 text-red-500 text-xs bg-red-50 rounded-lg px-3 py-2">
                <i className="ri-error-warning-line"></i>
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-[#c9a96e] hover:bg-[#b8944f] text-white font-semibold py-3 rounded-xl transition-all active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap text-sm"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  登录中...
                </>
              ) : (
                <>
                  <i className="ri-login-circle-line"></i>
                  登录
                </>
              )}
            </button>
          </form>

          <div className="mt-6 pt-4 border-t border-gray-100">
            <p className="text-[11px] text-gray-400 text-center leading-relaxed">
              如遇登录问题，请联系您的专属顾问
            </p>
          </div>
        </div>
      </div>

      {/* Copyright */}
      <footer className="mt-auto pt-8 pb-4 text-center w-full">
        <p className="text-[11px] text-gray-400">RTB 试管助孕中心</p>
        <p className="text-[10px] text-gray-300 mt-0.5">客户全周期管理 CRM 系统</p>
        <p className="text-[10px] text-gray-300 mt-1">© 2026 RTB Fertility. All Rights Reserved.</p>
      </footer>
    </div>
  );
}